import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { t } from '../../constants/i18n';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { SLIDE_IN_BOTTOM } from '@/constants/animation';
import { useDiscountSummary } from '../../hooks/cart/useDiscountSummary';
import { DiscountSummaryPanel } from './DiscountSummaryPanel';
import { useCartDiscountCode } from '../../hooks/cart/useCartDiscountCode';

interface DiscountPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * DiscountPanel
 * - 匹配 Cart Drawer 宽度（max-w-[440px]）
 * - 底部滑入动画使用统一动画常量（SLIDE_IN_BOTTOM）
 */
export const DiscountPanel: React.FC<DiscountPanelProps> = ({ isOpen, onClose }) => {
    const [discountCode, setDiscountCode] = useState('');

    // 使用 Hook 获取折扣汇总数据和动作
    const discountSummary = useDiscountSummary();
    // 从 Store 获取状态与动作（权威来源）
    const { discountCode: discountState, applyDiscountCode } = useCartDiscountCode();

    const handleApply = async () => {
        const trimmed = discountCode.trim();
        if (!trimmed) return;

        const result = await applyDiscountCode(trimmed);
        if (result.ok) {
            // emitToast({
            //     type: 'success',
            //     message: t('couponApplied'),
            //     messageKey: 'couponApplied',
            //     context: { code: trimmed }
            // });
        }
    };

    return (
        <AnimatePresence mode="wait" initial={false}>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <m.div
                        key="discount-backdrop"
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm touch-none"
                        style={{ zIndex: 2147483649 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={onClose}
                    />

                    {/* Panel */}
                    <m.div
                        key="discount-panel"
                        variants={SLIDE_IN_BOTTOM}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="fixed bottom-0 right-0 w-screen max-w-full sm:max-w-[440px] bg-card flex flex-col shadow-2xl touch-pan-y overscroll-contain rounded-t-2xl overflow-hidden min-w-0"
                        style={{
                            zIndex: 2147483650,
                            touchAction: 'pan-y',
                            overscrollBehaviorX: 'none',
                            overscrollBehaviorY: 'contain',
                            maxHeight: '90vh'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Card className="flex flex-col rounded-none border-0 shadow-none bg-transparent overflow-hidden">
                            {/* Header */}
                            <CardHeader className="flex-row items-center justify-between gap-2 px-4 py-4 border-b space-y-0">
                                <CardTitle className="text-lg font-semibold">
                                    {t('couponTitle')}
                                </CardTitle>
                                <button
                                    onClick={onClose}
                                    className="p-1 hover:bg-accent rounded-full transition-colors"
                                    aria-label={t('back')}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </CardHeader>

                            {/* Content */}
                            <CardContent className="overflow-y-auto px-4 pb-4">
                                <div className="max-w-md mx-auto pt-6 space-y-4">
                                    {/* Description */}
                                    <p className="text-sm text-muted-foreground text-center">
                                        {t('couponDescription')}
                                    </p>

                                    {/* Input */}
                                    <div className="space-y-3">
                                        <Input
                                            type="text"
                                            placeholder={t('couponPlaceholder')}
                                            value={discountCode}
                                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                                            disabled={discountSummary.discountCode.isValidating}
                                            className="uppercase bg-muted focus:bg-background my-4! h-14! py-4! border-input! border! border-solid! rounded-md! px-4!"
                                            style={{ fontSize: '16px' }}
                                        />

                                        {/* Apply Button */}
                                        <Button
                                            onClick={handleApply}
                                            disabled={discountSummary.discountCode.isValidating || !discountCode.trim()}
                                            className="w-full bg-black! text-white! font-bold! text-lg! hover:bg-gray-800! transition-colors rounded-md!"
                                            size="lg"
                                        >
                                            {discountSummary.discountCode.isValidating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            {discountSummary.discountCode.isValidating ? t('applying') : t('applyCoupon')}
                                        </Button>
                                    </div>

                                    {/* Status Messages */}
                                    {discountState.error && (
                                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-2">
                                            <X className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                                            <p className="text-sm text-destructive flex-1">{discountState.error}</p>
                                        </div>
                                    )}

                                    {(discountSummary.discounts.length > 0 || discountSummary.totalDiscountCents > 0) && (
                                        <DiscountSummaryPanel
                                            totalDiscountCents={discountSummary.totalDiscountCents}
                                            discounts={discountSummary.discounts}
                                            currency={discountSummary.currency}
                                            showDiscountCodeBadge={discountSummary.discountCode.isValid}
                                            discountCode={discountSummary.discountCode.code}
                                            showLineItemRemove={false}
                                            theme="red"
                                        />
                                    )}

                                    {/* Instructions */}
                                    <div className="mt-8 pt-8 border-t">
                                        <h3 className="font-semibold text-sm mb-2">{t('howToUse')}</h3>
                                        <ul className="text-sm text-muted-foreground space-y-2">
                                            <li>• {t('couponStep1')}</li>
                                            <li>• {t('couponStep2')}</li>
                                            <li>• {t('couponStep3')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </m.div>
                </>
            )}
        </AnimatePresence>
    );
};
